// frontend/src/pages/ResponsesPage.tsx - UPDATED FOR HOOKS/STORE INTEGRATION
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// FIXED: Use entity-specific hooks
import {
  useResponses,
  useResponse,
  useCreateResponse,
  useUpdateResponse,
  useDeleteResponse,
  useApproveResponse,
  useRejectResponse,
  useSendResponse
} from '@/hooks/useResponses';

import { useCorrespondences } from '@/hooks/useCorrespondences';
import { useWorkflows } from '@/hooks/useWorkflows';
import { useUsers } from '@/hooks/useUsers';
import { CreateResponseModal } from '@/components/responses/CreateResponseModal';

// FIXED: Import API types
import type {
  Response,
  Correspondence,
  Workflow,
  User,
  NormalizedUser,
  ResponseQueryParams,
  ResponseCreateRequest,
  ResponseUpdateRequest,
  ResponseStatus
} from '@/api/types';

// ===== UTILITY FUNCTIONS =====

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== ERROR BOUNDARY =====

class ResponsesErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('ResponsesPage Error Boundary:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ResponsesPage Error:', error, errorInfo);
    
    // Report error to app store for global error handling
    const { setGlobalError } = useAppStore.getState();
    setGlobalError(`Responses Page Error: ${error.message}`);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                Page Error
              </h3>
              <p className="text-red-700 dark:text-red-300 text-sm">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <Button 
                onClick={() => this.setState({ hasError: false })} 
                size="sm" 
                variant="secondary"
                className="mt-3"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ===== STATUS BADGE COMPONENT =====

const StatusBadge: React.FC<{ status: ResponseStatus }> = ({ status }) => {
  // FIXED: Use the actual ResponseStatus values from your API types
  const statusConfig: Record<ResponseStatus, { color: string; label: string }> = {
    draft: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300', label: 'Draft' },
    in_review: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300', label: 'In Review' },
    approved: { color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300', label: 'Approved' },
    sent: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300', label: 'Sent' },
    scheduled: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300', label: 'Scheduled' },
    cancelled: { color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300', label: 'Cancelled' }
  };

  const config = statusConfig[status] || statusConfig.draft;
  
  return (
    <span className={cn('px-2 py-1 text-xs font-medium rounded-full', config.color)}>
      {config.label}
    </span>
  );
};

// ===== PRIORITY BADGE COMPONENT =====

const PriorityBadge: React.FC<{ priority?: string }> = ({ priority = 'normal' }) => {
  const priorityConfig = {
    low: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300', label: 'Low' },
    normal: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300', label: 'Normal' },
    high: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300', label: 'High' },
    critical: { color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300', label: 'Critical' }
  };

  const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.normal;
  
  return (
    <span className={cn('px-2 py-1 text-xs font-medium rounded-full', config.color)}>
      {config.label}
    </span>
  );
};

// ===== RESPONSE LIST ITEM COMPONENT =====

const ResponseListItem: React.FC<{
  response: Response;
  correspondences: Correspondence[];
  users: User[];
  isSelected: boolean;
  onSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onSend: (id: string) => void;
  onDelete: (id: string) => void;
  isUpdating?: boolean;
  isDeleting?: boolean;
}> = ({
  response,
  correspondences,
  users,
  isSelected,
  onSelect,
  onApprove,
  onReject,
  onSend,
  onDelete,
  isUpdating = false,
  isDeleting = false
}) => {
  const { confirm } = useConfirmation();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Safe ID extraction
  const responseId = response._id || response.id || '';

  // Find related correspondence
  const correspondence = correspondences.find(c => 
    (c._id || c.id) === response.correspondenceId
  );

  // Find assigned user
  const assignedUser = users.find(u => 
    (u._id || u.id) === (response as any).assignedTo
  );

  const handleSelect = useCallback(() => {
    onSelect(responseId);
  }, [responseId, onSelect]);

  const handleApprove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onApprove(responseId);
  }, [responseId, onApprove]);

  const handleReject = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRejectDialog(true);
  }, []);

  const handleConfirmReject = useCallback(() => {
    if (rejectReason.trim()) {
      onReject(responseId, rejectReason.trim());
      setShowRejectDialog(false);
      setRejectReason('');
    }
  }, [responseId, rejectReason, onReject]);

  const handleSend = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onSend(responseId);
  }, [responseId, onSend]);

  const handleDelete = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = await confirm({
      title: 'Delete Response',
      message: 'Are you sure you want to delete this response? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      onDelete(responseId);
    }
  }, [responseId, onDelete, confirm]);

  if (!responseId) {
    console.warn('ResponseListItem: Invalid response ID:', response);
    return null;
  }

  // Resolve source correspondences
  const sources = (response as any).sourceIds || [];
  const populatedSources = sources.filter((s: any) => typeof s === 'object' && s !== null);
  const primarySource = populatedSources[0] || correspondence;

  // Resolve project
  const projects = (response as any).projectIds || [];
  const populatedProjects = projects.filter((p: any) => typeof p === 'object' && p !== null);

  // Resolve workflow
  const wf = (response as any).workflowId;
  const workflowName = (wf && typeof wf === 'object') ? wf.name : '';

  // Resolve creator
  const creator = (response as any).userId;
  const creatorName = creator && typeof creator === 'object'
    ? (creator.firstName ? `${creator.firstName} ${creator.lastName || ''}`.trim() : creator.username)
    : '';

  return (
    <ResponsesErrorBoundary>
      <div
        className={cn(
          'px-4 py-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700/50',
          isSelected && 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
        )}
        onClick={handleSelect}
      >
        {/* Row 1: Key columns */}
        <div className="flex items-center gap-3 mb-1.5">
          {/* Project */}
          {populatedProjects.length > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-xs font-medium flex-shrink-0">
              {populatedProjects[0].projectName || 'Project'}
            </span>
          )}

          {/* Source Correspondence(s) */}
          {populatedSources.length > 0 ? (
            <div className="flex items-center gap-1 min-w-0 flex-shrink">
              {populatedSources.map((src: any, i: number) => (
                <span key={src._id || i} className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300 max-w-[200px] truncate">
                  {src.documentCode || truncateText(src.subject, 30) || 'Correspondence'}
                </span>
              ))}
            </div>
          ) : correspondence ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300 max-w-[200px] truncate">
              {truncateText(correspondence.subject, 30) || 'Correspondence'}
            </span>
          ) : null}

          {/* Workflow */}
          {workflowName && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs font-medium flex-shrink-0">
              {workflowName}
            </span>
          )}

          {/* Stage */}
          {(response as any).currentStage && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 text-xs font-medium flex-shrink-0">
              {(response as any).currentStage}
            </span>
          )}

          {/* Status */}
          <StatusBadge status={response.status} />
        </div>

        {/* Row 2: Subject of primary source correspondence */}
        <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate mb-1">
          {(() => {
            // Prefer Document Subject custom field from primary source correspondence
            const docSubject = (primarySource?.customFields || []).find((f: any) => f.fieldId === 'document_subject');
            return truncateText((docSubject?.value) || primarySource?.subject || response.mailSubject || 'No Subject', 100);
          })()}
        </h3>

        {/* Row 3: Meta - Created by, Last activity */}
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            <span>Created: {creatorName ? `${creatorName}, ` : ''}{formatDate(response.createdAt)}</span>
            {response.updatedAt && response.updatedAt !== response.createdAt && (
              <span>Last activity: {formatDate(response.updatedAt)}</span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
            {(response as any).canApprove && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleApprove}
                  disabled={isUpdating || isDeleting}
                  className="text-green-600 hover:text-green-700"
                >
                  {isUpdating ? <LoadingSpinner size="sm" /> : 'Approve'}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleReject}
                  disabled={isUpdating || isDeleting}
                  className="text-red-600 hover:text-red-700"
                >
                  Reject
                </Button>
              </>
            )}

            {response.status === 'approved' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleSend}
                disabled={isUpdating || isDeleting}
                className="text-blue-600 hover:text-blue-700"
              >
                Send
              </Button>
            )}

            {response.canDelete && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleDelete}
                disabled={isUpdating || isDeleting}
                className="text-red-600 hover:text-red-700"
              >
                {isDeleting ? <LoadingSpinner size="sm" /> : 'Delete'}
              </Button>
            )}
          </div>
        </div>

        {/* Reject Dialog */}
        {showRejectDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowRejectDialog(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Reject Response
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Please provide a reason for rejecting this response:
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                rows={3}
                placeholder="Enter rejection reason..."
              />
              <div className="flex items-center justify-end space-x-3 mt-4">
                <Button
                  variant="secondary"
                  onClick={() => setShowRejectDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmReject}
                  disabled={!rejectReason.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Reject
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResponsesErrorBoundary>
  );
};

// ===== RESPONSE LIST COMPONENT =====

const ResponseList: React.FC<{
  responses: Response[];
  correspondences: Correspondence[];
  users: User[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onSend: (id: string) => void;
  onDelete: (id: string) => void;
  loading: boolean;
  pagination?: any;
  onPageChange?: (page: number) => void;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  updatingResponse?: string | null;
  deletingResponse?: string | null;
  summary?: any;
}> = ({
  responses,
  correspondences,
  users,
  selectedId,
  onSelect,
  onApprove,
  onReject,
  onSend,
  onDelete,
  loading,
  pagination,
  onPageChange,
  onNextPage,
  onPrevPage,
  updatingResponse,
  deletingResponse,
  summary
}) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <ResponsesErrorBoundary>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Summary Header */}
        {summary && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  {summary.totalResponses || responses.length}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
              </div>
              <div>
                <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                  {summary.draftResponses || responses.filter(r => r.status === 'draft').length}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Drafts</div>
              </div>
              <div>
                <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                  {summary.inReviewResponses || responses.filter(r => r.status === 'in_review').length}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Pending</div>
              </div>
              <div>
                <div className="text-xl font-bold text-green-600 dark:text-green-400">
                  {summary.sentResponses || responses.filter(r => r.status === 'sent').length}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Sent</div>
              </div>
            </div>
          </div>
        )}

        {/* List Content */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {responses.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-4">📧</div>
              <p className="text-lg mb-2 text-gray-500 dark:text-gray-400">No responses found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Create responses to manage email replies and approvals.
              </p>
            </div>
          ) : (
            responses.map((response) => {
              const responseId = response._id || response.id || '';
              if (!responseId) {
                console.warn('Skipping invalid response:', response);
                return null;
              }
              
              return (
                <ResponseListItem
                  key={responseId}
                  response={response}
                  correspondences={correspondences}
                  users={users}
                  isSelected={selectedId === responseId}
                  onSelect={onSelect}
                  onApprove={onApprove}
                  onReject={onReject}
                  onSend={onSend}
                  onDelete={onDelete}
                  isUpdating={updatingResponse === responseId}
                  isDeleting={deletingResponse === responseId}
                />
              );
            })
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
              </div>
              
              <nav className="flex space-x-1">
                <Button
                  onClick={onPrevPage}
                  disabled={!pagination.hasPrevPage}
                  variant="secondary"
                  size="sm"
                >
                  Previous
                </Button>
                
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = pagination.currentPage <= 3 ? i + 1 : pagination.currentPage + i - 2;
                  if (pageNum > pagination.totalPages || pageNum < 1) return null;
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => onPageChange?.(pageNum)}
                      variant={pagination.currentPage === pageNum ? "primary" : "secondary"}
                      size="sm"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                
                <Button
                  onClick={onNextPage}
                  disabled={!pagination.hasNextPage}
                  variant="secondary"
                  size="sm"
                >
                  Next
                </Button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </ResponsesErrorBoundary>
  );
};

// ===== RESPONSE DETAIL COMPONENT =====

const ResponseDetail: React.FC<{
  response: Response;
  correspondence?: Correspondence;
  workflow?: Workflow;
  users: User[];
  onEdit: (response: Response) => void;
  onDelete: (id: string) => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onSend: (id: string) => void;
  loading: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
}> = ({
  response,
  correspondence,
  workflow,
  users,
  onEdit,
  onDelete,
  onApprove,
  onReject,
  onSend,
  loading,
  isUpdating = false,
  isDeleting = false
}) => {
  const { confirm } = useConfirmation();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Safe ID extraction
  const responseId = response._id || response.id || '';

  // Find assigned user
  const assignedUser = users.find(u => 
    (u._id || u.id) === (response as any).assignedTo
  );

  const handleEdit = useCallback(() => {
    onEdit(response);
  }, [response, onEdit]);

  const handleDelete = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Delete Response',
      message: 'Are you sure you want to delete this response? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      onDelete(responseId);
    }
  }, [responseId, onDelete, confirm]);

  const handleApprove = useCallback(() => {
    onApprove(responseId);
  }, [responseId, onApprove]);

  const handleReject = useCallback(() => {
    setShowRejectDialog(true);
  }, []);

  const handleConfirmReject = useCallback(() => {
    if (rejectReason.trim()) {
      onReject(responseId, rejectReason.trim());
      setShowRejectDialog(false);
      setRejectReason('');
    }
  }, [responseId, rejectReason, onReject]);

  const handleSend = useCallback(() => {
    onSend(responseId);
  }, [responseId, onSend]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <ResponsesErrorBoundary>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 h-full overflow-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                {(() => {
                  const src = (response as any).sourceCorrespondences?.[0]?.correspondenceId;
                  const docSubject = (src?.customFields || []).find((f: any) => f.fieldId === 'document_subject');
                  return (docSubject?.value) || response.mailSubject || 'No Subject';
                })()}
              </h2>
              
              <div className="flex items-center space-x-2 mb-2">
                <StatusBadge status={response.status} />
                <PriorityBadge priority={(response as any).priority} />
              </div>

              {/* Meta information */}
              <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <div>Created: {formatDate(response.createdAt)}</div>
                {assignedUser && (
                  <div>Assigned to: {assignedUser.firstName || assignedUser.username}</div>
                )}
                {workflow && (
                  <div>Workflow: {workflow.name}</div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2 ml-4">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleEdit}
                disabled={isUpdating || isDeleting}
              >
                Edit
              </Button>

              {(response as any).canApprove && (
                <>
                  <Button
                    size="sm"
                    onClick={handleApprove}
                    disabled={isUpdating || isDeleting}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isUpdating ? <LoadingSpinner size="sm" /> : 'Approve'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleReject}
                    disabled={isUpdating || isDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Reject
                  </Button>
                </>
              )}

              {response.status === 'approved' && (
                <Button
                  size="sm"
                  onClick={handleSend}
                  disabled={isUpdating || isDeleting}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Send
                </Button>
              )}

              {response.canDelete && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleDelete}
                  disabled={isUpdating || isDeleting}
                  className="text-red-600 hover:text-red-700"
                >
                  {isDeleting ? <LoadingSpinner size="sm" /> : 'Delete'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Original Correspondence Reference */}
          {correspondence && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                Original Correspondence
              </h3>
              <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                <div><strong>Subject:</strong> {correspondence.subject}</div>
                <div><strong>Created:</strong> {formatDate(correspondence.createdAt)}</div>
              </div>
            </div>
          )}

          {/* Response Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Response Content
            </label>
            <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-md">
              <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                {response.mailBody || 'No content'}
              </div>
            </div>
          </div>
        </div>

        {/* Reject Dialog */}
        {showRejectDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowRejectDialog(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Reject Response
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Please provide a reason for rejecting this response:
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                rows={3}
                placeholder="Enter rejection reason..."
              />
              <div className="flex items-center justify-end space-x-3 mt-4">
                <Button
                  variant="secondary"
                  onClick={() => setShowRejectDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmReject}
                  disabled={!rejectReason.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Reject
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResponsesErrorBoundary>
  );
};

// ===== MAIN RESPONSES PAGE COMPONENT =====

export const ResponsesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Store integration
  const { 
    globalError, 
    setGlobalError, 
    clearGlobalError,
    setApiLoading,
    clearApiLoading
  } = useAppStore();
  
  const { user: currentUser } = useAuthStore();

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ResponseStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Mutation states
  const [updatingResponse, setUpdatingResponse] = useState<string | null>(null);
  const [deletingResponse, setDeletingResponse] = useState<string | null>(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Debounced search for better performance
  const debouncedSearchTerm = useDebounced(searchTerm, 500);

  // Query parameters
  const queryParams = useMemo((): ResponseQueryParams => ({
    page: currentPage,
    limit: 20,
    search: debouncedSearchTerm || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  }), [debouncedSearchTerm, statusFilter, currentPage]);

  // API hooks
  const {
    data: responsesData,
    loading: responsesLoading,
    error: responsesError,
    refetch: refetchResponses
  } = useResponses(queryParams);

  const {
    data: selectedResponse,
    loading: selectedResponseLoading,
    error: selectedResponseError
  } = useResponse(undefined);

  const {
    data: correspondencesData
  } = useCorrespondences();

  const {
    data: workflowsData
  } = useWorkflows();

  const {
    data: usersData
  } = useUsers();

  // Mutation hooks
  const approveResponseMutation = useApproveResponse({
    onSuccess: () => {
      clearGlobalError();
      refetchResponses();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to approve response');
    }
  });

  const rejectResponseMutation = useRejectResponse({
    onSuccess: () => {
      clearGlobalError();
      refetchResponses();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to reject response');
    }
  });

  const sendResponseMutation = useSendResponse({
    onSuccess: () => {
      clearGlobalError();
      refetchResponses();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to send response');
    }
  });

  const deleteResponseMutation = useDeleteResponse({
    onSuccess: () => {
      clearGlobalError();
      refetchResponses();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to delete response');
    }
  });

  // Extract data safely
  const responses = responsesData?.data || [];
  const pagination = responsesData?.pagination;
  const summary = responsesData?.summary || responsesData?.analytics;
  const correspondences = correspondencesData?.data || [];
  const workflows = workflowsData?.data || [];
  const users = usersData?.data || [];

  // Find related data for selected response
  const selectedCorrespondence = selectedResponse ? correspondences.find(c => 
    (c._id || c.id) === selectedResponse.correspondenceId
  ) : undefined;

  const selectedWorkflow = selectedResponse ? workflows.find(w => 
    (w._id || w.id) === (selectedResponse as any).workflowId
  ) : undefined;

  // Error handling
  useEffect(() => {
    if (responsesError) {
      setGlobalError(responsesError);
    } else {
      clearGlobalError();
    }
  }, [responsesError, setGlobalError, clearGlobalError]);

  // Loading state management
  useEffect(() => {
    if (responsesLoading || 
        selectedResponseLoading ||
        approveResponseMutation.loading || 
        rejectResponseMutation.loading || 
        sendResponseMutation.loading ||
        deleteResponseMutation.loading) {
      setApiLoading('responses', true);
    } else {
      setApiLoading('responses', false);
    }
    
    return () => clearApiLoading();
  }, [
    responsesLoading,
    selectedResponseLoading,
    approveResponseMutation.loading,
    rejectResponseMutation.loading,
    sendResponseMutation.loading,
    deleteResponseMutation.loading,
    setApiLoading,
    clearApiLoading
  ]);

  // Event handlers
  const handleSelect = useCallback((responseId: string) => {
    // Navigate to full detail page instead of split view
    navigate(`/responses/${responseId}`);
  }, [navigate]);

  const handleApprove = useCallback(async (responseId: string) => {
    try {
      setUpdatingResponse(responseId);
      // FIXED: Pass object with id and comment as required by the mutation
      await approveResponseMutation.mutate({ 
        id: responseId, 
        comment: 'Response approved' // Default approval comment
      });
    } finally {
      setUpdatingResponse(null);
    }
  }, [approveResponseMutation]);

  const handleReject = useCallback(async (responseId: string, reason: string) => {
    try {
      setUpdatingResponse(responseId);
      // FIXED: Pass object with correct structure
      await rejectResponseMutation.mutate({ id: responseId, comment: reason });
    } finally {
      setUpdatingResponse(null);
    }
  }, [rejectResponseMutation]);

  const handleSend = useCallback(async (responseId: string) => {
    try {
      setUpdatingResponse(responseId);
      await sendResponseMutation.mutate(responseId);
    } finally {
      setUpdatingResponse(null);
    }
  }, [sendResponseMutation]);

  const handleDelete = useCallback(async (responseId: string) => {
    try {
      setDeletingResponse(responseId);
      await deleteResponseMutation.mutate(responseId);
    } finally {
      setDeletingResponse(null);
    }
  }, [deleteResponseMutation]);

  const handleEdit = useCallback((response: Response) => {
    // Navigate to edit mode - would need ResponseEditor component
    const responseId = response._id || response.id;
    navigate(`/responses/${responseId}/edit`);
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    clearGlobalError();
    refetchResponses();
  }, [clearGlobalError, refetchResponses]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleNextPage = useCallback(() => {
    if (pagination?.hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [pagination]);

  const handlePrevPage = useCallback(() => {
    if (pagination?.hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  }, [pagination]);

  // Actions for page header
  const actions = useMemo(() => (
    <ResponsesErrorBoundary>
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <Input
            placeholder="Search responses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="in_review">In Review</option>
            <option value="approved">Approved</option>
            <option value="sent">Sent</option>
            <option value="scheduled">Scheduled</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <Button
          onClick={handleRefresh}
          variant="secondary"
          disabled={responsesLoading}
        >
          {responsesLoading ? <LoadingSpinner size="sm" /> : 'Refresh'}
        </Button>
        
        <Button
          onClick={() => setShowCreateModal(true)}
          disabled={responsesLoading}
        >
          Create Response
        </Button>
      </div>
    </ResponsesErrorBoundary>
  ), [
    searchTerm,
    statusFilter,
    responsesLoading,
    handleRefresh
  ]);

  // Main render
  return (
    <ResponsesErrorBoundary>
      <PageLayout 
        title="Responses" 
        subtitle={`Manage email responses and approval workflows • ${summary?.totalResponses || responses.length} total`}
        actions={actions}
        loading={responsesLoading && responses.length === 0}
      >
        {/* Global Error Display */}
        {globalError && (
          <div className="mb-6">
            <ErrorAlert 
              message={globalError} 
              onClose={clearGlobalError}
            />
          </div>
        )}

        {/* Main Content - Full Width List */}
        <div className="max-w-6xl mx-auto">
          <ResponseList
            responses={responses}
            correspondences={correspondences}
            users={users}
            selectedId={undefined}
            onSelect={handleSelect}
            onApprove={handleApprove}
            onReject={handleReject}
            onSend={handleSend}
            onDelete={handleDelete}
            loading={responsesLoading}
            pagination={pagination}
            onPageChange={handlePageChange}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            updatingResponse={updatingResponse}
            deletingResponse={deletingResponse}
            summary={summary}
          />
        </div>
      </PageLayout>

      {/* Create Response Modal */}
      <CreateResponseModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(newResponse) => {
          setShowCreateModal(false);
          navigate(`/responses/${newResponse._id || newResponse.id}`);
          refetchResponses();
        }}
      />
    </ResponsesErrorBoundary>
  );
};